import { MC_TEXT_COLORS } from '@/app/lib/minecraft/font';
import { toRuns, type Run } from '@/app/lib/minecraft/formatCodes';

// Text the game stores with § codes (world names, item custom names, build titles), rendered as styled
// spans inside site UI: headings, cards, tooltips. Unlike FormattedText (the tool previews) it is static
// and site-legible:
// - § only — `&` is a plain character here (see `sectionOnly` in formatCodes).
// - §k shows the real text, not a 70ms scramble: a name in a heading or one of 1,000 library cards
//   must not animate.
// - A color too dark for the navy UI (black, dark blue, dark red, dark gray on #161B2E) is lightened
//   toward white until it reaches 3:1, keeping its hue. Plain text inherits the surrounding color.
// No codes → the bare string, so the common case adds no spans. Editable fields keep the raw codes.

const SURFACE = [0x16, 0x1b, 0x2e]; // --mc-surface-1, the darkest surface a name sits on besides the page
const lin = (c: number) => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; };
const lum = ([r, g, b]: number[]) => 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
const SURFACE_LUM = lum(SURFACE);

const legible = new Map<string, string>();
function legibleColor(color: string): string {
  const hex = color.startsWith('#') ? color : MC_TEXT_COLORS[color];
  if (!hex) return '';
  const cached = legible.get(hex);
  if (cached !== undefined) return cached;
  const rgb = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
  let out = rgb;
  for (let t = 0; t <= 1; t += 0.05) {
    out = rgb.map(c => Math.round(c + (255 - c) * t));
    if ((lum(out) + 0.05) / (SURFACE_LUM + 0.05) >= 3) break;
  }
  const css = `rgb(${out.join(' ')})`;
  legible.set(hex, css);
  return css;
}

/** Styled runs (e.g. an item's custom name from `readItemName`) with the same site-legible rules. */
export function RunsText({ runs }: { runs: Run[] }) {
  return (
    <>
      {runs.map((r, i) => {
        const deco = [r.underline ? 'underline' : '', r.strike ? 'line-through' : ''].filter(Boolean).join(' ');
        return (
          <span key={i} style={{
            color: r.color ? legibleColor(r.color) || undefined : undefined,
            fontWeight: r.bold ? 700 : undefined,
            fontStyle: r.italic ? 'italic' : undefined,
            textDecoration: deco || undefined,
          }}>{r.text}</span>
        );
      })}
    </>
  );
}

export default function SectionText({ text }: { text: string }) {
  if (!text.includes('§')) return <>{text}</>;
  return <RunsText runs={toRuns(text, { sectionOnly: true })} />;
}
